// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * D1 REST API client for remote collection access.
 *
 * Provides typed query/get/set methods matching the D1 REST endpoint
 * conventions. Used by remote hooks to fetch and mutate collection data
 * when a collection is in 'remote' mode (community tier).
 */

import type { CollectionModeConfig } from '../../config/CollectionModeConfig'

export interface D1QueryParams {
  selector?: Record<string, unknown>
  sort?: Array<Record<string, 'asc' | 'desc'>>
  limit?: number
  skip?: number
}

export class D1Client {
  constructor(private readonly config: CollectionModeConfig) {}

  async query<T = Record<string, unknown>>(
    collection: string,
    params: D1QueryParams = {},
  ): Promise<T[]> {
    const res = await this.fetch(`/${collection}-rest/v1/query`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
    const json = await res.json()
    return (json as { results?: T[] }).results ?? []
  }

  async get<T = Record<string, unknown>>(
    collection: string,
    ids: string[],
  ): Promise<T[]> {
    const res = await this.fetch(`/${collection}-rest/v1/get`, {
      method: 'POST',
      body: JSON.stringify({ ids }),
    })
    const json = await res.json()
    return (json as { results?: T[] }).results ?? []
  }

  async set<T = Record<string, unknown>>(
    collection: string,
    doc: Partial<T>,
  ): Promise<T> {
    const res = await this.fetch(`/${collection}-rest/v1/set`, {
      method: 'POST',
      body: JSON.stringify(doc),
    })
    return res.json() as Promise<T>
  }

  private async fetch(path: string, init: RequestInit): Promise<Response> {
    const token = await this.config.getAuthToken()
    const res = await globalThis.fetch(`${this.config.apiBaseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...init.headers,
      },
    })
    if (!res.ok) {
      throw new D1ClientError(res.status, await res.text(), path)
    }
    return res
  }
}

export class D1ClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
    public readonly path: string,
  ) {
    super(`D1 REST ${status}: ${path}`)
    this.name = 'D1ClientError'
  }
}
